'use client';
import { CSSProperties, FunctionComponent } from 'react';
import { Box, Typography } from '@mui/material';
import PageContainer from '../tableComponents/PageContainer';
import { ReactComponent as SpecSheetIcon } from '../../../static/product_image/specsheetIcon.svg';

import { ReactComponent as DataSheetIcon } from '../../../static/product_image/datasheetIcon.svg';
import { ReactComponent as YsimIcon } from '../../../static/product_image/Y-sim.svg';
import { ReactComponent as RohsIcon } from '../../../static/product_image/ROHS.svg';
import { ReactComponent as StepCadIcon } from '../../../static/product_image/Step-Cad Icon.svg';
import { ReactComponent as SpiceIcon } from '../../../static/product_image/spice.svg';
import { DetectedUniquePart } from '../../../schemas/DetectedUniquePart';
import { formatToKPlus } from '../../../utils/FormatInstock';
import { SupportInfo } from '../../../schemas/SupportInfo';
import ProductSampleButton from './RequestSampleButton';
import {
    getParameterFormattedValue,
    getParameterFormattedValueByParameterId,
} from '../../../utils/ParameterRelatedFunction';
import { ReactComponent as EmaIcon } from '../../../static/product_image/EMA Icon.svg';
import { LanguageMapping } from '../../../utils/LanguageMapping';
import { CS_BASE_URL, linkPrefix, NEW_CS_BASE_URL } from '../../../utils/ProjectUrls';
import { whatToDisplayForAliases } from './SingleProductPage';
import { useResponsive } from '../../../contexts/ResponsiveContext';
import {
    getDisplayNameForParameter,
    getDisplayNameForReturnParameterValue,
} from '../../../utils/StringRelated';
import { ProductsContent } from '../../../graphql/pimcore/modules/siteContent/types/ProductsContent';
import { formatCountIcuString } from '../../../utils/formatCount';
import { useLocale } from '../../../contexts/LocaleProvider';
import WarningIcon from '@mui/icons-material/Warning';
import { formatIcuString } from '../../../utils/Locale';
import { callLoadPartDiv } from '@/utils/kemetPlugin';
import { usePathname } from 'next/navigation';

interface ProductInfoDetailProps {
    data: DetectedUniquePart;
    totalQuantity: number;
    supportInfo: SupportInfo;
    locale: string;
    contentTree: ProductsContent;
}

const iconStyle = {
    marginRight: '15px',
    cursor: 'pointer',
    '&:hover path': {
        fill: '#E5673E',
    },
    display: 'flex',
    alignItems: 'center',
};

export const handleIconClick = (url: string) => {
    window.open(url, '_blank');
};

const ProductInfoDetail: FunctionComponent<ProductInfoDetailProps> = ({
    data,
    totalQuantity,
    supportInfo,
    locale,
    contentTree,
}) => {
    const language = LanguageMapping.get(locale) || 'ENGLISH';
    const { locale: pimCoreLocale } = useLocale();
    const pathname = usePathname();
    const whatAliasesToDisplay = whatToDisplayForAliases(data);

    console.log(
        'totalQuantity = ',
        totalQuantity,
        'formatToKPlus(totalQuantity) = ',
        formatToKPlus(totalQuantity)
    );

    const {
        isMaxScreen,
        isScreenXxl,
        isScreenXl,
        isScreenLg,
        isScreenMd,
        isScreenSm,
        isScreenXs,
        responsiveWidth,
    } = useResponsive();

    const leftProductInfoWidth = (() => {
        if (isMaxScreen) return '485px';
        if (isScreenXxl) return '485px';
        if (isScreenXl) return '485px';
        if (isScreenLg) return '400px';
        if (isScreenMd) return responsiveWidth;
        if (isScreenSm) return responsiveWidth;
        // 預設或 isScreenXs：
        return responsiveWidth;
    })();

    const rightProductInfoWidth = (() => {
        if (isMaxScreen) return '683px';
        if (isScreenXxl) return '683px';
        if (isScreenXl) return '683px';
        if (isScreenLg) return '420px';
        if (isScreenMd) return responsiveWidth;
        if (isScreenSm) return responsiveWidth;
        // 預設或 isScreenXs：
        return responsiveWidth;
    })();

    const imageStyle: CSSProperties = (() => {
        if (isMaxScreen || isScreenXxl || isScreenXl) {
            return { width: '368px', height: '230px', objectFit: 'contain' };
        } else if (isScreenLg || isScreenMd) {
            return { width: '320px', height: '200px', objectFit: 'contain' };
        } else if (isScreenSm) {
            return { width: '200px', height: '125px', objectFit: 'contain' };
        } else {
            return { width: '150px', height: '94px', objectFit: 'contain' };
        }
    })();

    // 當畫面為 isScreenMd 以下時，採用垂直排列
    const isMobileLayout = isScreenMd || isScreenSm || isScreenXs;
    const obsoleteDate: Date | null = data.obsoleteDate ? new Date(data.obsoleteDate) : null;

    const rohsValue = getParameterFormattedValue(data.parameterValues, 'RoHS');

    const productNameAndAlias = (
        <>
            <Typography
                component="h1"
                sx={{
                    color: '#B75332',
                    fontFamily: 'Poppins',
                    fontWeight: 700,
                    fontSize: '1.5rem',
                    lineHeight: '36px',
                    // 讓內層 <a> 完全繼承外層字型與顏色，移除預設底線
                    '& a': {
                        color: 'inherit',
                        font: 'inherit', // 同時繼承 family/size/weight/style/line-height
                        textDecoration: 'none',
                        lineHeight: 'inherit',
                    },
                    '& a:visited': { color: 'inherit' }, // 防止被瀏覽器變紫色
                }}
                data-testid={'displayPn-h1'}
            >
                <a href={pathname} data-testid={'displayPn-a'}>
                    {data.displayPn}
                </a>
            </Typography>
            {data.statusMessage && (
                <Typography
                    sx={{
                        color: '#B75332',
                        fontFamily: 'Poppins',
                        fontWeight: 500,
                        fontSize: '65%',
                    }}
                >
                    {data.statusMessage}
                </Typography>
            )}

            {obsoleteDate && (
                <Typography
                    sx={{
                        color: '#B75332',
                        fontFamily: 'Poppins',
                        fontWeight: 500,
                        fontSize: '65%',
                    }}
                >
                    {obsoleteDate > new Date()
                        ? `${contentTree.product.notForNewDesign.content}`
                        : `${contentTree.product.obsolete.content}`}
                </Typography>
            )}
            <Typography
                sx={{
                    color: '#B75332',
                    fontFamily: 'Poppins',
                    fontWeight: 400,
                    fontSize: '1rem',
                    lineHeight: '24px',
                }}
            >
                {whatAliasesToDisplay?.length ? `(${whatAliasesToDisplay.join(', ')})` : ''}
            </Typography>
        </>
    );

    /* 产品描述 */
    const productDescription = (
        <Typography
            data-testid={`${data.uniquePn}-description`}
            variant="h6"
            sx={{
                fontFamily: 'Poppins',
                fontWeight: 500,
                fontSize: '1rem',
                lineHeight: '24px',
                paddingTop: isScreenSm || isScreenXs ? 0 : '1rem',
                paddingBottom: '1rem',
            }}
        >
            {[
                data.series?.formattedValue,
                getParameterFormattedValue(data.parameterValues, 'Type'),
                getParameterFormattedValue(data.parameterValues, 'Capacitance'),
                getParameterFormattedValue(data.parameterValues, 'Capacitance Tolerance'),
                getParameterFormattedValue(data.parameterValues, 'Compare Voltage DC'),
                getParameterFormattedValue(data.parameterValues, 'Temperature Coefficient'),
                getParameterFormattedValue(data.parameterValues, 'Description'),
                getParameterFormattedValue(data.parameterValues, 'Chip Size'),
            ]
                .filter((value) => value !== '')
                .join(', ')}
        </Typography>
    );

    const spiceFile = getParameterFormattedValueByParameterId(data.parameterValues, 455);
    return (
        <PageContainer
            containerName={'product-info'}
            innerSx={{
                width: responsiveWidth,
                // 根據畫面大小決定排列方式
                flexDirection: isMobileLayout ? 'column' : 'row',
                justifyContent: isMobileLayout ? 'center' : 'space-between',
            }}
        >
            {/* 左侧部分 */}
            <Box sx={{ width: leftProductInfoWidth }} data-testid={'left-product-info'}>
                {!(isMobileLayout || isScreenMd) && productNameAndAlias}

                {!isMobileLayout && (
                    <>
                        {/* 产品图片 */}
                        <img
                            src={data.imageUrl}
                            alt="Product"
                            style={{ width: '368px', height: '230px', objectFit: 'contain' }}
                            data-testid={`${data.uniquePn}-img`}
                        />
                        {/* 库存和按钮 */}
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-start',
                                gap: '16px',
                                marginTop: '16px',
                                alignItems: 'center',
                            }}
                        >
                            <Typography
                                data-testid="stock"
                                variant="h6"
                                sx={{ fontWeight: 'bold', color: '#0E8A13' }}
                            >
                                {formatIcuString(
                                    contentTree.product.productsCountInStock.content,
                                    { count: formatToKPlus(totalQuantity) },
                                    pimCoreLocale
                                )}
                            </Typography>
                            <ProductSampleButton
                                partNumber={data.uniquePn}
                                contentTree={contentTree}
                            />
                        </Box>
                        {productDescription}
                    </>
                )}

                {isScreenMd && (
                    <Box
                        sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start' }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'flex-start',
                                gap: '16px',
                                flex: 1, // 讓文字區塊佔滿可用空間
                            }}
                        >
                            {productNameAndAlias}
                        </Box>
                        {/* 产品图片 */}
                        <img
                            src={data.imageUrl}
                            alt="Product"
                            style={imageStyle}
                            data-testid={`${data.uniquePn}-img`}
                        />
                        {/* 库存和按钮 */}
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-start',
                                gap: '16px',
                                flexDirection: 'column',
                            }}
                        >
                            <Typography
                                data-testid="stock"
                                variant="h6"
                                sx={{ fontWeight: 'bold', color: '#0E8A13' }}
                            >
                                {/*{`${formatToKPlus(totalQuantity)} ${contentTree.filters.checkboxLabels.inStock.content} `}*/}
                                {formatCountIcuString(
                                    contentTree.filters.checkboxLabels.inStock.content,
                                    totalQuantity,
                                    pimCoreLocale
                                )}
                            </Typography>
                            <ProductSampleButton
                                partNumber={data.uniquePn}
                                contentTree={contentTree}
                            />
                            {/* 产品描述 */}
                            {productDescription}
                        </Box>
                    </Box>
                )}

                {isScreenSm && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'flex-start',
                            alignItems: 'stretch', // 讓內部元素同高
                        }}
                    >
                        {/* 文字區塊 */}
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'flex-start',
                                gap: '16px',
                                flex: 1, // 讓文字區塊佔滿可用空間
                            }}
                        >
                            {productNameAndAlias}

                            <Typography
                                data-testid="stock"
                                variant="h6"
                                sx={{ fontWeight: 'bold', color: '#0E8A13' }}
                            >
                                {/*{`${formatToKPlus(totalQuantity)} ${contentTree.filters.checkboxLabels.inStock.content}`}*/}
                                {formatCountIcuString(
                                    contentTree.filters.checkboxLabels.inStock.content,
                                    totalQuantity,
                                    pimCoreLocale
                                )}
                            </Typography>
                            <ProductSampleButton
                                partNumber={data.uniquePn}
                                contentTree={contentTree}
                            />
                            {productDescription}
                        </Box>
                        {/* 图片區塊 */}
                        <Box sx={{ flex: 1 }}>
                            <img
                                src={data.imageUrl}
                                alt="Product"
                                // 將圖片高度設定為 100%，寬度自動調整
                                style={imageStyle}
                                data-testid={`${data.uniquePn}-img`}
                            />
                        </Box>
                    </Box>
                )}

                {isScreenXs && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'flex-start',
                        }}
                    >
                        {/* 文字區塊 */}
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'flex-start',
                                gap: '5px',
                                flex: 1, // 讓文字區塊佔滿可用空間
                            }}
                        >
                            {productNameAndAlias}
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                <img
                                    src={data.imageUrl}
                                    alt="Product"
                                    style={{ ...imageStyle, display: 'block' }}
                                    data-testid={`${data.uniquePn}-img`}
                                />
                            </Box>

                            <Typography
                                data-testid="stock"
                                variant="h6"
                                sx={{ fontWeight: 'bold', color: '#0E8A13' }}
                            >
                                {/*{`${formatToKPlus(totalQuantity)} ${contentTree.filters.checkboxLabels.inStock.content}`}*/}
                                {formatCountIcuString(
                                    contentTree.filters.checkboxLabels.inStock.content,
                                    totalQuantity,
                                    pimCoreLocale
                                )}
                            </Typography>
                            <ProductSampleButton
                                partNumber={data.uniquePn}
                                contentTree={contentTree}
                            />
                            {productDescription}
                        </Box>
                    </Box>
                )}

                {/* Key Resources */}
                <Typography
                    variant="subtitle1"
                    sx={{
                        fontFamily: 'Poppins',
                        fontWeight: 500,
                        fontSize: '1rem',
                        lineHeight: '1rem',
                    }}
                >
                    {contentTree.product.keyResources.content}:
                </Typography>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        width: '100%',
                        height: '57px',
                        paddingBottom: isScreenXs ? '85px' : 0,
                    }}
                    data-testid={'key-resources-items'}
                >
                    {getParameterFormattedValue(data.parameterValues, 'Specsheet Suppression') !==
                        'true' && (
                        <Box
                            sx={iconStyle}
                            onClick={() =>
                                handleIconClick(
                                    `${NEW_CS_BASE_URL}/component-documentation/download/specsheet/${encodeURIComponent(data.uniquePn)}?lang=${locale}`
                                )
                            }
                        >
                            <SpecSheetIcon title="SpecSheet" />
                            {contentTree.product.specsheet.content}
                        </Box>
                    )}
                    {supportInfo.dataSheet && (
                        <Box
                            sx={iconStyle}
                            onClick={() =>
                                handleIconClick(
                                    `${NEW_CS_BASE_URL}/component-documentation/download/datasheet/${encodeURIComponent(data.uniquePn)}?lang=${locale}`
                                )
                            }
                        >
                            <DataSheetIcon title="DataSheet" />
                            {contentTree.product.datasheet.content}
                        </Box>
                    )}

                    {/*Y-SIM needs to be moved from 4th to the 3rd position on Individual PN Page. (Asset Links sort order should be in this order: Specsheet, Datasheet, Y-SIM, SPICE, STEP, ECAD, RoHS)*/}
                    {getParameterFormattedValue(data.parameterValues, 'KSIM Support') === 'Yes' && (
                        <Box
                            sx={iconStyle}
                            onClick={() =>
                                handleIconClick(
                                    `https://ksim3.kemet.com/capacitor-simulation?pn=${encodeURIComponent(data.uniquePn)}`
                                )
                            }
                        >
                            <YsimIcon title="Y-SIM" />
                            Y-SIM
                        </Box>
                    )}

                    {getParameterFormattedValueByParameterId(data.parameterValues, 364) ===
                        'Yes' && (
                        <Box
                            sx={iconStyle}
                            onClick={() =>
                                handleIconClick(
                                    `https://elc.kemet.com/?part=${encodeURIComponent(data.uniquePn)}`
                                )
                            }
                        >
                            <YsimIcon title="Y-SIM" />
                            Y-SIM
                        </Box>
                    )}

                    {getParameterFormattedValue(data.parameterValues, 'STEP') && (
                        <Box
                            sx={iconStyle}
                            onClick={() =>
                                handleIconClick(
                                    `${NEW_CS_BASE_URL}/component-documentation/download/step/${encodeURIComponent(data.uniquePn)}.step`
                                )
                            }
                        >
                            <StepCadIcon title="StepCad" />
                            STEP/CAD
                        </Box>
                    )}
                    <Box
                        sx={{
                            ...iconStyle,
                            '& a': {
                                color: 'black !important',
                                textDecoration: 'none',
                            },
                            '& a:visited': {
                                color: 'black !important',
                            },
                        }}
                    >
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                const ok = callLoadPartDiv({
                                    manufacturer: data.mfg,
                                    partNumber: data.uniquePn,
                                    language: 'en-US',
                                });
                                console.log(
                                    '[EDA/MCAD anchor] returned:',
                                    ok,
                                    'typeof window.loadPartDiv =',
                                    typeof window !== 'undefined' &&
                                        typeof (window as any).loadPartDiv
                                );
                                if (!ok) alert('EDA/MCAD 插件尚未就緒（loadPartDiv 不存在）');
                            }}
                            style={{ textDecoration: 'none', color: 'black' }}
                        >
                            <EmaIcon title="Ema" /> EDA/MCAD
                        </a>
                    </Box>
                    {rohsValue === 'Yes' && (
                        <Box
                            sx={iconStyle}
                            onClick={() =>
                                handleIconClick(
                                    `${linkPrefix}/component-documentation/download/rohs/${encodeURIComponent(data.uniquePn)}.pdf`
                                )
                            }
                        >
                            <RohsIcon title="Rohs" />
                            RoHS
                        </Box>
                    )}
                    {rohsValue === 'No' && data.mfgId === 19 && (
                        <Box
                            sx={iconStyle}
                            onClick={() =>
                                handleIconClick(
                                    `${linkPrefix}/content/Resource%20Library/Quality%20Document/KEMET-Statement-Proposition-65-Rev-May-2024.pdf`
                                )
                            }
                        >
                            <WarningIcon style={{ color: '#E5E53E' }} />
                            Prop 65
                        </Box>
                    )}
                    {(rohsValue === 'With Exemptions' || rohsValue === 'No') &&
                        data.mfgId === 19 && (
                            <Box
                                sx={iconStyle}
                                onClick={() =>
                                    handleIconClick(
                                        `${linkPrefix}/content/Resource%20Library/Quality%20Document/REACH_SVHC_Declaration.pdf`
                                    )
                                }
                            >
                                <WarningIcon style={{ color: '#E5E53E' }} />
                                SHVC
                            </Box>
                        )}
                    {spiceFile && spiceFile === 'true' && (
                        <Box
                            sx={iconStyle}
                            onClick={() =>
                                handleIconClick(
                                    `${CS_BASE_URL}/query/spice/${encodeURIComponent(data.uniquePn)}`
                                )
                            }
                        >
                            <SpiceIcon title="Spice" />
                            {contentTree.product.spiceFile.content}
                        </Box>
                    )}
                    {spiceFile && spiceFile === 's2p' && (
                        <Box
                            sx={iconStyle}
                            onClick={() =>
                                handleIconClick(
                                    `${CS_BASE_URL}/query/spice/${encodeURIComponent(data.uniquePn)}`
                                )
                            }
                        >
                            <YsimIcon title="Spice" />
                            S-Parameter
                        </Box>
                    )}
                </Box>
            </Box>

            {/* 右侧部分 */}
            <Box
                sx={{ width: rightProductInfoWidth, boxSizing: 'border-box' }}
                data-testid={'right-product-info'}
            >
                <Typography
                    sx={{
                        color: '#B75332',
                        fontFamily: 'Poppins',
                        fontWeight: 400,
                        borderBottom: '1px solid #e0e0e0',
                        fontSize: '1.125rem',
                        lineHeight: '30px',
                    }}
                >
                    {contentTree.product.specifications.content}
                </Typography>

                {/* 属性列表 */}
                {data.parameterValues && (
                    <Box
                        sx={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            boxSizing: 'border-box',
                        }}
                    >
                        {data.parameterValues
                            .filter(
                                (param) =>
                                    !new Set([296, 152, 313, 498, 455, 497]).has(param.parameterId)
                            )
                            .map((param) => (
                                <Box
                                    key={param.id}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center', // 由 center 改為 flex-start，避免換行後垂直置中問題
                                        minHeight: '24px', // 改用 minHeight，容器可自動延伸
                                        padding: '1px 0px',
                                        borderBottom: '1px solid #e0e0e0',
                                        fontFamily: 'Poppins',
                                        fontWeight: 400,
                                        fontSize: '1rem',
                                        lineHeight: '30px',
                                    }}
                                >
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontFamily: 'Poppins',
                                            fontWeight: 400,
                                            fontSize: '1rem',
                                            lineHeight: '24px',
                                        }}
                                    >
                                        {getDisplayNameForParameter(param, language)}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontFamily: 'Poppins',
                                            fontWeight: 400,
                                            fontSize: '1rem',
                                            lineHeight: '24px',
                                            textAlign: 'end',
                                        }}
                                    >
                                        {getDisplayNameForReturnParameterValue(param, language)}
                                    </Typography>
                                </Box>
                            ))}
                    </Box>
                )}
            </Box>
        </PageContainer>
    );
};

export default ProductInfoDetail;
